import { cn } from '@/lib/utils';

type ChartNoDataVariant = 'donut' | 'bar' | 'area';

interface ChartNoDataProps {
  variant: ChartNoDataVariant;
  message?: string;
  className?: string;
  /** Chart color used for the placeholder. Defaults to --chart-1. */
  color?: string;
}

const DEFAULT_COLOR = 'hsl(var(--chart-1))';

function PlaceholderDonut({ color }: { color: string }) {
  return (
    <svg viewBox='0 0 200 200' className='mx-auto aspect-square max-h-[200px]' aria-hidden='true'>
      <circle cx='100' cy='100' r='70' fill='none' stroke={color} strokeWidth='30' opacity={0.2} />
    </svg>
  );
}

function PlaceholderBars({ color }: { color: string }) {
  const bars = [
    { width: '75%', y: 15 },
    { width: '90%', y: 55 },
    { width: '55%', y: 95 },
    { width: '65%', y: 135 },
  ];

  return (
    <svg viewBox='0 0 300 175' className='h-[200px] w-full' aria-hidden='true'>
      {bars.map((bar, i) => (
        <rect
          key={i}
          x='0'
          y={bar.y}
          width={bar.width}
          height='24'
          rx='4'
          fill={color}
          opacity={0.15}
        />
      ))}
    </svg>
  );
}

function PlaceholderArea({ color }: { color: string }) {
  return (
    <svg
      viewBox='0 0 400 200'
      className='h-[240px] w-full'
      preserveAspectRatio='none'
      aria-hidden='true'
    >
      <path
        d='M 0 160 C 50 140, 80 100, 120 110 S 180 60, 220 80 S 300 40, 340 70 S 380 90, 400 60'
        fill='none'
        stroke={color}
        strokeWidth='2'
        opacity={0.25}
      />
      <path
        d='M 0 160 C 50 140, 80 100, 120 110 S 180 60, 220 80 S 300 40, 340 70 S 380 90, 400 60 L 400 200 L 0 200 Z'
        fill={color}
        opacity={0.07}
      />
    </svg>
  );
}

const VARIANT_MAP: Record<ChartNoDataVariant, React.FC<{ color: string }>> = {
  donut: PlaceholderDonut,
  bar: PlaceholderBars,
  area: PlaceholderArea,
};

export function ChartNoData({
  variant,
  message = 'No data available',
  className,
  color = DEFAULT_COLOR,
}: ChartNoDataProps) {
  const Placeholder = VARIANT_MAP[variant];

  return (
    <div className={cn('relative', className)}>
      <div className='pointer-events-none select-none' aria-hidden='true'>
        <Placeholder color={color} />
      </div>
      <div className='absolute inset-0 flex items-center justify-center'>
        <p className='rounded-lg bg-white/40 px-4 py-1.5 text-[13px] font-medium text-muted-foreground shadow-[0_1px_6px_rgba(0,0,0,0.06)] ring-1 ring-white/60 backdrop-blur-md dark:bg-white/10 dark:ring-white/15'>
          {message}
        </p>
      </div>
    </div>
  );
}
